import { Box, Grid, TextField, type SxProps, type Theme } from '@mui/material';
import { SideDrawer, Typography } from '@peritasai/ui-components';
import { SubmitButton } from '../shared/SubmitButton';
import type { FacilityEntity } from '@peritasai/repository';
import { useCreateFacility, useUpdateFacility } from '@peritasai/repository';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DrawerMode } from '../../utils/drawer';

export { DrawerMode as FacilityDrawerMode };

interface FacilityDrawerProps {
  open: boolean;
  mode: DrawerMode;
  selectedFacility: FacilityEntity | null;
  onClose: () => void;
  onSuccess?: () => void;
}

interface FormState {
  name: string;
  address: string;
  startTime: string;
  endTime: string;
  timeZone: string;
  contactInfo: string;
}

interface FormErrors {
  name?: string;
  address?: string;
  startTime?: string;
  endTime?: string;
  timeZone?: string;
  apiError?: string;
}

const timePickerSx: SxProps<Theme> = {
  '& input[type="time"]::-webkit-calendar-picker-indicator': {
    cursor: 'pointer',
    opacity: 0.5,
    '&:hover': { opacity: 1 },
  },
  '& input[type="time"]': { letterSpacing: '0.05em' },
};

const detectTimezone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return '';
  }
};

const toTimeInput = (time: string | null | undefined): string => {
  if (!time || !time.trim()) return '';
  const match = time.match(/(\d{1,2}):(\d{2})/);
  if (!match) return '';
  return `${match[1].padStart(2, '0')}:${match[2]}`;
};

const emptyForm = (): FormState => ({
  name: '',
  address: '',
  startTime: '',
  endTime: '',
  timeZone: detectTimezone(),
  contactInfo: '',
});

function validate(
  form: FormState,
  t: (key: string, options?: Record<string, string>) => string
): FormErrors {
  const errors: FormErrors = {};
  if (!form.name.trim()) {
    errors.name = t('pages.entityManagement.drawer.fieldRequired', {
      fieldName: t('pages.entityManagement.facilityDrawer.nameLabel'),
    });
  }
  if (!form.address.trim()) {
    errors.address = t('pages.entityManagement.drawer.fieldRequired', {
      fieldName: t('pages.entityManagement.facilityDrawer.addressLabel'),
    });
  }
  if (!form.startTime) {
    errors.startTime = t('pages.entityManagement.drawer.fieldRequired', {
      fieldName: t('pages.entityManagement.facilityDrawer.startTimeLabel'),
    });
  }
  if (!form.endTime) {
    errors.endTime = t('pages.entityManagement.drawer.fieldRequired', {
      fieldName: t('pages.entityManagement.facilityDrawer.endTimeLabel'),
    });
  } else if (form.startTime && form.endTime <= form.startTime) {
    // String comparison is valid for same-day HH:MM ranges; overnight hours (e.g. 22:00–06:00) are not supported.
    errors.endTime = t('pages.entityManagement.facilityDrawer.endTimeAfterStart');
  }
  if (!form.timeZone.trim()) {
    errors.timeZone = t('pages.entityManagement.drawer.fieldRequired', {
      fieldName: t('pages.entityManagement.facilityDrawer.timeZoneLabel'),
    });
  } else if (!/^(UTC|GMT|[^\s/]+\/\S+)$/.test(form.timeZone.trim())) {
    errors.timeZone = t('pages.entityManagement.facilityDrawer.timeZoneFormat');
  }
  return errors;
}

export const FacilityDrawer = ({
  open,
  mode,
  selectedFacility,
  onClose,
  onSuccess,
}: FacilityDrawerProps) => {
  const { t } = useTranslation(['periverse']);
  const [form, setForm] = useState<FormState>(emptyForm());
  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState(false);

  const createMutation = useCreateFacility();
  const updateMutation = useUpdateFacility();
  const isPending = createMutation.isPending || updateMutation.isPending;

  const entityName = t('pages.entityManagement.facilityDrawer.entityName');

  useEffect(() => {
    if (!open) return;
    if (mode === DrawerMode.Edit && selectedFacility) {
      setForm({
        name: selectedFacility.name ?? '',
        address: selectedFacility.address ?? '',
        startTime: toTimeInput(selectedFacility.startTime),
        endTime: toTimeInput(selectedFacility.endTime),
        timeZone: selectedFacility.timeZone ?? '',
        contactInfo: selectedFacility.contactInfo ?? '',
      });
    } else {
      setForm(emptyForm());
    }
    setErrors({});
    setTouched(false);
  }, [open, mode, selectedFacility]);

  const handleChange =
    (field: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const next = { ...form, [field]: e.target.value };
      setForm(next);
      if (touched) {
        setErrors(validate(next, t));
      }
    };

  const handleSubmit = async () => {
    setTouched(true);
    const validationErrors = validate(form, t);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    setErrors({});

    try {
      if (mode === DrawerMode.Create) {
        await createMutation.mutateAsync({
          name: form.name.trim(),
          address: form.address.trim(),
          startTime: form.startTime,
          endTime: form.endTime,
          timeZone: form.timeZone.trim(),
          ...(form.contactInfo.trim() && { contactInfo: form.contactInfo.trim() }),
        });
        setForm(emptyForm());
        setTouched(false);
        onSuccess?.();
      } else if (mode === DrawerMode.Edit && selectedFacility) {
        await updateMutation.mutateAsync({
          id: selectedFacility.id,
          dto: {
            name: form.name.trim(),
            address: form.address.trim(),
            startTime: form.startTime,
            endTime: form.endTime,
            timeZone: form.timeZone.trim(),
            contactInfo: form.contactInfo.trim() || null,
          },
        });
        onSuccess?.();
        onClose();
      }
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : t('pages.entityManagement.drawer.errorUnexpected');
      setErrors((prev) => ({ ...prev, apiError: message }));
    }
  };

  return (
    <SideDrawer
      open={open}
      title={
        mode === DrawerMode.Create
          ? t('pages.entityManagement.drawer.titleCreate', { entityName })
          : t('pages.entityManagement.drawer.titleEdit', { entityName })
      }
      onClose={onClose}
      footer={
        <SubmitButton disabled={isPending} onClick={() => void handleSubmit()}>
          {isPending
            ? t('pages.entityManagement.drawer.submitting')
            : t('pages.entityManagement.drawer.submit')}
        </SubmitButton>
      }
    >
      <Grid container spacing={2}>
        <Grid size={12}>
          <TextField
            label={`${t('pages.entityManagement.facilityDrawer.nameLabel')} *`}
            fullWidth
            size="small"
            value={form.name}
            onChange={handleChange('name')}
            error={touched && !!errors.name}
            helperText={touched ? errors.name : undefined}
            slotProps={{ formHelperText: { sx: { ml: '5px' } } }}
          />
        </Grid>

        <Grid size={12}>
          <TextField
            label={`${t('pages.entityManagement.facilityDrawer.addressLabel')} *`}
            fullWidth
            size="small"
            multiline
            rows={3}
            value={form.address}
            onChange={handleChange('address')}
            error={touched && !!errors.address}
            helperText={touched ? errors.address : undefined}
            slotProps={{ formHelperText: { sx: { ml: '5px' } } }}
          />
        </Grid>

        <Grid size={12}>
          <Typography variant="body2Strong" sx={{ color: 'primary.main' }}>
            {t('pages.entityManagement.facilityDrawer.operatingHoursLabel')}
          </Typography>
        </Grid>

        <Grid size={6}>
          <Box lang="en-GB">
            <TextField
              label={`${t('pages.entityManagement.facilityDrawer.startTimeLabel')} *`}
              type="time"
              fullWidth
              size="small"
              value={form.startTime}
              onChange={handleChange('startTime')}
              error={touched && !!errors.startTime}
              helperText={touched ? errors.startTime : undefined}
              sx={timePickerSx}
              slotProps={{
                inputLabel: { shrink: true },
                htmlInput: { step: 60 },
                formHelperText: { sx: { ml: '5px' } },
              }}
            />
          </Box>
        </Grid>

        <Grid size={6}>
          <Box lang="en-GB">
            <TextField
              label={`${t('pages.entityManagement.facilityDrawer.endTimeLabel')} *`}
              type="time"
              fullWidth
              size="small"
              value={form.endTime}
              onChange={handleChange('endTime')}
              error={touched && !!errors.endTime}
              helperText={touched ? errors.endTime : undefined}
              sx={timePickerSx}
              slotProps={{
                inputLabel: { shrink: true },
                htmlInput: { step: 60, ...(form.startTime && { min: form.startTime }) },
                formHelperText: { sx: { ml: '5px' } },
              }}
            />
          </Box>
        </Grid>

        <Grid size={12}>
          <TextField
            label={`${t('pages.entityManagement.facilityDrawer.timeZoneLabel')} *`}
            fullWidth
            size="small"
            value={form.timeZone}
            onChange={handleChange('timeZone')}
            error={touched && !!errors.timeZone}
            helperText={touched ? errors.timeZone : undefined}
            slotProps={{ formHelperText: { sx: { ml: '5px' } } }}
          />
        </Grid>

        <Grid size={12}>
          <TextField
            label={t('pages.entityManagement.facilityDrawer.contactInfoLabel')}
            fullWidth
            size="small"
            value={form.contactInfo}
            onChange={handleChange('contactInfo')}
          />
        </Grid>
      </Grid>

      {touched && errors.apiError && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" sx={{ color: 'error.main', mt: 0.5 }}>
            {errors.apiError}
          </Typography>
        </Box>
      )}
    </SideDrawer>
  );
};
